/**
 * MONSTER TOWN — CUSTOMERS. OWNED BY THE VENUE CREW (src/venue/**).
 *
 * Monsters walk in, sit down, ask for something in pictures, and wait.
 *
 * LAW #2 IS THE WHOLE DESIGN OF THIS FILE: there is no patience meter, no
 * timer, no leaving in a huff. A customer that has ordered sits happily until
 * it is served, forever, and nothing anywhere counts down. The only clock in
 * here decides how soon the NEXT customer strolls in, and it never makes
 * anything worse — a busier shop is a reward, not a difficulty.
 */

import * as THREE from 'three'
import type { PaletteName } from '../core/palette'
import type { SceneNode } from '../core/scene'
import type { InstanceId, MonsterConfig, Vec3 } from '../core/types'
// ONE CHARACTER SYSTEM. `buildMonster` is a pure config-in / group-out factory
// and `MonsterAnimator` poses the rig it returns — the same pair the NPC crew
// uses. Reaching for them here is deliberate: a customer must be built by the
// same code as the monster standing next to it, or the two drift apart in
// silhouette and shading and the customers stop reading as monsters at all.
import { MonsterAnimator } from '../monster/anim'
import { buildMonster, facingBiasedToCamera, orientFace, type MonsterRig } from '../monster/art'
import { customerLook, heartsBurst, waitRing } from './art'
import { SEAT_TOP, TABLE_SEAT_OFFSET } from './fixtures'
import { makeOrder, availableItems, seatPlacements } from './orders'
import type { VenueDef, VenueOrder, VenueTray } from './types'
import { trayIsEmpty, trayMatches } from './types'

type Phase = 'walkingIn' | 'sitting' | 'waiting' | 'eating' | 'leaving'

interface Customer {
  key: string
  look: MonsterConfig
  rig: MonsterRig
  anim: MonsterAnimator
  /** The floor ring that says "this one is waiting". Colour = pairing. */
  mark: THREE.Group | null
  hearts: THREE.Group | null
  order: VenueOrder | null
  seatId: InstanceId | null
  seatPos: Vec3
  sitY: number
  /** Yaw the monster holds while seated: square on to its own table. */
  sitFacing: number
  phase: Phase
  t: number
  x: number
  z: number
  bob: number
  facing: number
}

/** Seconds between customers strolling in. Never a penalty, only a rhythm. */
const SPAWN_EVERY = 7
/** After a happy serve, a friend turns up almost immediately. */
const FRIEND_DELAY = 1.4
const WALK_SPEED = 2.6

/**
 * Seat geometry, in tiles. IMPORTED, NOT RETYPED: the seat height and the table
 * chair offset are facts about the furniture, so they come from the file that
 * builds it. Stating them twice is how they drifted apart before.
 */
const TABLE_SEAT_X = TABLE_SEAT_OFFSET
const TABLE_SEAT_Y = SEAT_TOP
const STOOL_SEAT_Y = SEAT_TOP

/** The `crouch` the `sitChair` action holds. Mirrors `ACTIONS.sitChair`. */
const SIT_CROUCH = 0.95

/**
 * HOW FAR THE BODY SINKS INTO THE CUSHION, in tiles.
 *
 * Hips exactly level with the seat board is geometrically right and reads as a
 * monster hovering a hair over it, because our seats have a soft pad on top. A
 * few centimetres down and the cushion visibly takes the weight.
 */
const SIT_SINK = 0.05

/**
 * ...and how far FORWARD, so what legs a monster does have hang over the front
 * lip of the seat instead of being buried inside the cushion.
 *
 * A LONE CHAIR ONLY. At a table "forward" is straight into the tabletop, and
 * the clearance between a seated body and the edge of the top is the one number
 * that stops a table being drawn through a customer's chest. Under a table the
 * legs are hidden by the table anyway, which is exactly what legs under a table
 * look like.
 */
const SIT_FORWARD = 0.14

/**
 * PUT THE HIPS ON THE SEAT, NOT THE FEET.
 *
 * `rig.root` is authored with the FEET on y=0, and sitting only moves the body:
 * the pose drops `bounce` by `crouch * hipDrop` and swings the legs forward, so
 * the hips end up at `root.y + legH - crouch * hipDrop`. Setting `root.y` to the
 * seat height therefore stands a monster's feet on the stool and leaves its
 * backside a whole leg-length above it — which is what the captured frame
 * showed: three customers hovering over their stools, and the tall one's torso
 * lifted into the tabletop.
 *
 * Solve for the root instead. `hipDrop` is `legH * 0.9`, so this lands a hair
 * BELOW the seat surface for every body shape in the table, short-legged or not.
 */
function sitLift(rig: { hipDrop: number }): number {
  const legH = rig.hipDrop / 0.9
  return SIT_CROUCH * rig.hipDrop - legH - SIT_SINK
}

export interface ServeOutcome {
  result: 'none' | 'empty' | 'correct' | 'wrong'
  order: VenueOrder | null
  scoops: number
  /** Which customer. Lets the ticket rail retire the RIGHT card, not the oldest. */
  key?: string
}

/** Everything the ticket rail needs to draw one order and point at its monster. */
export interface OrderPosted {
  key: string
  order: VenueOrder
  look: MonsterConfig
}

/**
 * Where an open order's monster is standing, in WORLD space, at the point the
 * order card should hang from — just clear of the top of its head.
 *
 * The service loop projects these to canvas pixels every frame so the card
 * follows the monster that asked for it. See `VenueAnchor` in `events.ts` for
 * why the cards are no longer pinned in a row at the top of the screen.
 */
export interface CustomerAnchor {
  key: string
  color: PaletteName
  x: number
  y: number
  z: number
}

export class VenueCrowd {
  private node: SceneNode | null = null
  private readonly list: Customer[] = []
  private spawnIn = 1.2
  private seq = 0

  constructor(
    private readonly def: VenueDef,
    private readonly rng: () => number,
  ) {}

  attach(node: SceneNode): void {
    this.node = node
  }

  /** Everyone goes home (leaving the venue, loading a save, switching slots). */
  clear(): void {
    for (const c of this.list) this.dispose(c)
    this.list.length = 0
    this.spawnIn = 1.2
  }

  count(): number {
    return this.list.length
  }

  waitingCount(): number {
    return this.list.filter((c) => c.phase === 'waiting' && c.order !== null).length
  }

  /**
   * Harness only: what each customer is actually doing. Exists because "are they
   * sitting?" is not answerable from a screenshot of a chunky monster at iso —
   * the pose has to be read off the rig, not eyeballed.
   */
  debugPoses(): { phase: string; action: string | null; y: number; shape: string }[] {
    return this.list.map((c) => ({
      phase: c.phase,
      action: c.anim.currentAction,
      y: Number(c.rig.root.position.y.toFixed(3)),
      shape: c.look.shape,
    }))
  }

  freeSeats(): number {
    return this.seats().length - this.list.filter((c) => c.seatId !== null).length
  }

  // -- seating ------------------------------------------------------------

  private seats(): { id: InstanceId; pos: Vec3; sitY: number; facing: number }[] {
    const node = this.node
    if (node === null) return []
    const t = node.grid.tileSize
    return seatPlacements(node, this.def).map((p) => {
      const c = node.grid.footprintCenter(p.anchor, p.footprint, p.rot)
      const wide = p.footprint.w > 1 || p.footprint.d > 1
      // Tables seat you on the left-hand stool; a lone stool you sit right on.
      //
      // THE OFFSET IS LOAD-BEARING. It has to clear the tabletop, not just land
      // near it: a seated monster's chest is at exactly tabletop height, so any
      // overlap at all does not read as "sitting close" — it reads as the
      // monster being run through by the table, which is what the captured frame
      // showed on two of the three customers. `TABLE_SEAT_X` matches the stool
      // positions in `fixtures.ts` and clears the tabletop radius there with a
      // margin wider than the fattest torso in `SHAPES`.
      const local = wide ? { x: -TABLE_SEAT_X * t, z: 0 } : { x: 0, z: 0 }
      const r = rotateXZ(local.x, local.z, p.rot)
      // Sitting AT a table means facing it. A stool on its own faces the room.
      const localFacing = wide ? Math.PI / 2 : 0
      const facing = localFacing + (p.rot * Math.PI) / 2
      // Sit on the FRONT of the cushion, not dead centre of it — see SIT_FORWARD.
      const fwd = wide ? 0 : SIT_FORWARD * t
      return {
        id: p.instanceId,
        pos: { x: c.x + r.x + Math.sin(facing) * fwd, y: 0, z: c.z + r.z + Math.cos(facing) * fwd },
        sitY: (wide ? TABLE_SEAT_Y : STOOL_SEAT_Y) * t,
        facing,
      }
    })
  }

  private freeSeat(): { id: InstanceId; pos: Vec3; sitY: number; facing: number } | null {
    const taken = new Set(this.list.map((c) => c.seatId))
    for (const s of this.seats()) if (!taken.has(s.id)) return s
    return null
  }

  // -- spawning -----------------------------------------------------------

  /** Bring one in now if there is anywhere to sit. Returns true if one came. */
  spawnNow(): boolean {
    const node = this.node
    if (node === null) return false
    const seat = this.freeSeat()
    if (seat === null) return false

    const door = node.grid.cellToWorld(this.def.doorCell)
    const look = customerLook(this.rng)
    // `detail: 'simple'` is the NPC budget — FEWER SPHERE SEGMENTS, not fewer
    // features. It used to drop the mouth, the blush and every outline, and a
    // customer sitting a hand's width from the kid's own monster read as a lump
    // with a sticker on it while the player had a face. Same face, cheaper mesh.
    const rig = buildMonster(look, { detail: 'simple' })
    rig.root.position.set(door.x, 0, door.z)
    node.actors.add(rig.root)
    const anim = new MonsterAnimator(rig, this.rng)

    this.list.push({
      key: `cust${++this.seq}`,
      look,
      rig,
      anim,
      mark: null,
      hearts: null,
      order: null,
      seatId: seat.id,
      seatPos: seat.pos,
      sitY: seat.sitY + sitLift(rig),
      sitFacing: seat.facing,
      phase: 'walkingIn',
      t: 0,
      x: door.x,
      z: door.z,
      bob: this.rng() * 6,
      facing: 0,
    })
    return true
  }

  /** A happy customer told a friend. */
  hurryNext(): void {
    this.spawnIn = Math.min(this.spawnIn, FRIEND_DELAY)
  }

  /** Every waiting customer waves, so a kid can spot who is still to serve. */
  hint(): void {
    for (const c of this.list) {
      if (c.phase !== 'waiting') continue
      c.t = Math.min(c.t, 0.01) // re-pops the marker over their head
      c.anim.play('wave', 1.1)
    }
  }

  // -- serving ------------------------------------------------------------

  /**
   * Hand over what the kid is carrying at a seat. NOTHING here can fail: a
   * wrong order gives the tray back with a giggle and leaves the customer
   * sitting happily with the same order still up.
   */
  serveAt(seatId: InstanceId, tray: VenueTray): ServeOutcome {
    const c = this.list.find((x) => x.seatId === seatId && x.phase === 'waiting')
    if (c === undefined || c.order === null) return { result: 'none', order: null, scoops: 0 }
    if (trayIsEmpty(tray)) {
      // A shrug and a re-pop of the marker: "still me, still waiting."
      c.t = 0.01
      c.anim.play('shrug', 0.9)
      return { result: 'empty', order: c.order, scoops: 0, key: c.key }
    }
    if (!trayMatches(tray, c.order)) {
      c.t = 0.01
      c.anim.play('shrug', 0.9)
      return { result: 'wrong', order: c.order, scoops: c.order.scoops.length, key: c.key }
    }
    const order = c.order
    c.phase = 'eating'
    c.t = 0
    this.dropPip(c)
    c.anim.play('eat', 1.8)
    const hearts = heartsBurst()
    hearts.position.set(c.x, c.sitY + 1.1, c.z)
    this.node?.actors.add(hearts)
    c.hearts = hearts
    return { result: 'correct', order, scoops: order.scoops.length, key: c.key }
  }

  /** The order currently up at a seat, for the harness and for hints. */
  orderAt(seatId: InstanceId): VenueOrder | null {
    return this.list.find((x) => x.seatId === seatId)?.order ?? null
  }

  /** Any waiting order at all — used by the harness demo and drive steps. */
  anyWaiting(): { seatId: InstanceId; order: VenueOrder; key: string } | null {
    for (const c of this.list) {
      if (c.phase === 'waiting' && c.order !== null && c.seatId !== null) {
        return { seatId: c.seatId, order: c.order, key: c.key }
      }
    }
    return null
  }

  // -- simulation ---------------------------------------------------------

  update(dt: number, servedCount: number, onOrdered: (o: OrderPosted) => void): void {
    const node = this.node
    if (node === null) return

    this.spawnIn -= dt
    if (this.spawnIn <= 0) {
      this.spawnIn = SPAWN_EVERY
      this.spawnNow()
    }

    const door = node.grid.cellToWorld(this.def.doorCell)
    for (let i = this.list.length - 1; i >= 0; i--) {
      const c = this.list[i]
      if (c === undefined) continue
      c.t += dt
      c.bob += dt
      c.anim.update(dt)

      switch (c.phase) {
        case 'walkingIn': {
          if (this.walkTo(c, c.seatPos.x, c.seatPos.z, dt)) {
            c.phase = 'sitting'
            c.t = 0
            c.anim.setSpeed(0)
          }
          break
        }
        case 'sitting': {
          c.anim.setSpeed(0)
          // NOT on the frame they arrive. The animator drops any action while
          // `speed > 0.25`, and speed only eases down over about six frames, so
          // a `play('sit')` issued the instant a customer stopped walking was
          // cancelled again before it blended in — every customer stood bolt
          // upright at its stool and then started idle-fidgeting, because a
          // released action also re-arms the fidget timer.
          if (c.t > 0.25) this.holdSit(c)
          if (c.t > 0.7) {
            const order = makeOrder(this.def, availableItems(node, this.def), servedCount, this.rng)
            c.order = order
            c.phase = 'waiting'
            c.t = 0
            if (order !== null) {
              // The ORDER itself now hangs over this monster's head as a card
              // anchored to it (see `anchors()`), so all the room needs is a
              // colour-matched ring on the floor saying "this one is waiting" —
              // the same colour the card wears. A ring is under the monster, so
              // unlike the old floating plate it can never cover a face.
              c.mark = waitRing(c.look.color as PaletteName)
              node.actors.add(c.mark)
              onOrdered({ key: c.key, order, look: c.look })
            }
          }
          break
        }
        case 'waiting': {
          // Waits forever, on purpose. Nothing here can time out (LAW #2).
          c.anim.setSpeed(0)
          this.holdSit(c)
          break
        }
        case 'eating': {
          if (c.t > 1.8) {
            c.phase = 'leaving'
            c.seatId = null
            c.t = 0
            c.anim.stop() // stand back up out of the sit pose
            if (c.hearts !== null) {
              c.hearts.removeFromParent()
              c.hearts = null
            }
          }
          break
        }
        case 'leaving': {
          if (this.walkTo(c, door.x, door.z, dt) || c.t > 14) {
            this.dispose(c)
            this.list.splice(i, 1)
          }
          break
        }
      }
    }
  }

  /**
   * Keep a seated customer seated — but only when nothing else is playing, so a
   * wave or a shrug still gets to finish. `currentAction` going null is also how
   * we notice the pose was dropped (or that the idle fidget is about to make a
   * sitting monster hop), which makes this self-healing rather than a one-shot.
   */
  private holdSit(c: Customer): void {
    if (c.anim.currentAction === null) c.anim.play('sitChair')
  }

  private walkTo(c: Customer, tx: number, tz: number, dt: number): boolean {
    const dx = tx - c.x
    const dz = tz - c.z
    const dist = Math.hypot(dx, dz)
    if (dist < 0.06) return true
    const step = Math.min(dist, WALK_SPEED * dt)
    c.x += (dx / dist) * step
    c.z += (dz / dist) * step
    c.facing = Math.atan2(dx, dz)
    // The rig's own walk cycle — legs and arms, not a sine bob on the whole body.
    c.anim.setSpeed(1)
    return false
  }

  /** Placement and facing. The RIG does the animating (see `anim.update`). */
  renderFrame(camYaw: number): void {
    for (const c of this.list) {
      const sitting = c.phase === 'waiting' || c.phase === 'eating' || c.phase === 'sitting'
      c.rig.root.position.set(c.x, sitting ? c.sitY : 0, c.z)
      // Sit facing your own table — but never with your back to the kid.
      //
      // Turning a customer to square up with its table is what a monster would
      // do, and on the far side of the room it showed the camera the back of a
      // head: you cannot tell who is waiting, or read the face you are supposed
      // to be making happy. Turning them to the LENS instead is what the old box
      // customers did, and a creature that pivots to follow a 90 degree camera
      // snap reads as a cardboard stand-up.
      //
      // So: the table pose is the target, allowed to drift up to `FACE_LIMIT`
      // off it — and no further from the camera than that. Close tables keep
      // their natural angle; far ones glance over a shoulder. Never a spin.
      c.rig.root.rotation.y = sitting ? facingBiasedToCamera(c.sitFacing, FACE_LIMIT, camYaw) : c.facing
      /**
       * Re-aim the FACE in the same frame the body moved.
       *
       * `MonsterAnimator.apply` already does this for every monster in the game,
       * but the customers' animator ticks in the venue's sim step while their
       * bodies are placed here in the render pass — so on a camera rotation
       * their faces would lag the turn by a step. One float write, no lag.
       */
      orientFace(c.rig, camYaw)
      if (c.mark !== null) {
        // Pops on arrival (and again on a hint), then breathes. This is the
        // thing that draws a 3-year-old's eye to who still needs serving, and it
        // lies on the floor UNDER the monster, so it cannot hide the face it
        // exists to point at.
        const pop = Math.min(1, c.t * 4)
        const s = (0.45 + 0.55 * (1 - Math.pow(1 - pop, 3))) * (1 + Math.sin(c.bob * 2.4) * 0.04)
        c.mark.scale.set(s, 1, s)
        c.mark.position.set(c.x, 0.02, c.z)
      }
      if (c.hearts !== null) {
        c.hearts.position.set(c.x, c.sitY + 1.05, c.z)
        c.hearts.rotation.y = camYaw
        for (let i = 0; i < c.hearts.children.length; i++) {
          const h = c.hearts.children[i]
          if (h === undefined) continue
          const p = Math.max(0, Math.min(1, c.t / 1.8 - i * 0.08))
          h.position.set((i - 2) * 0.16, p * 0.9, 0)
          h.scale.setScalar(Math.max(0.001, Math.sin(p * Math.PI) * 0.9))
        }
      }
    }
  }

  /**
   * Where each open order's card should hang: dead centre over the monster,
   * just clear of the top of its head. Only WAITING customers appear — a
   * customer who has been served has no card, so its anchor must vanish on the
   * same frame the order does.
   */
  anchors(): CustomerAnchor[] {
    const out: CustomerAnchor[] = []
    for (const c of this.list) {
      if (c.phase !== 'waiting' || c.order === null) continue
      out.push({
        key: c.key,
        color: c.look.color,
        x: c.x,
        // THE TOP OF THE HEAD OF A *SEATED* MONSTER, not a standing one.
        //
        // `rig.height` is the standing height, and sitting drops the body by
        // `SIT_CROUCH * hipDrop` (see `sitLift`). Using the standing height put
        // the leader line's dot a clear gap above the head on every customer,
        // and further off on the short-legged shapes than the tall ones — the
        // marker looked like it belonged to nobody. Same two numbers the sit
        // pose itself uses, so they can only ever move together.
        y: c.sitY + c.rig.height - SIT_CROUCH * c.rig.hipDrop + 0.1,
        z: c.z,
      })
    }
    return out
  }

  private dropPip(c: Customer): void {
    if (c.mark !== null) {
      c.mark.removeFromParent()
      c.mark = null
    }
  }

  private dispose(c: Customer): void {
    this.dropPip(c)
    c.hearts?.removeFromParent()
    c.hearts = null
    c.rig.root.removeFromParent()
  }
}

/**
 * How far a seated monster's BODY may be turned away from the camera, in
 * radians. 1.15 (about 66 degrees) is a three-quarter view, and the rig's own
 * face turn (`orientFace`) takes the remaining ~45 degrees off, so both eyes
 * land square on regardless of which way the table faces.
 */
const FACE_LIMIT = 1.15

/** Rotate a local offset by a quarter-turn count (clockwise from above). */
function rotateXZ(x: number, z: number, rot: 0 | 1 | 2 | 3): { x: number; z: number } {
  switch (rot) {
    case 1:
      return { x: -z, z: x }
    case 2:
      return { x: -x, z: -z }
    case 3:
      return { x: z, z: -x }
    default:
      return { x, z }
  }
}
